import React, { useState, useRef, useEffect } from "react";
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from "@stripe/react-stripe-js";
import { FaLock, FaCheckCircle, FaCreditCard, FaCalendarAlt, FaShieldAlt, FaTimes } from "react-icons/fa";
import Confetti from "react-confetti";
import confetti from "canvas-confetti";
import visacard from "./Image/VISA-logo.png";
import mastercard from "./Image/mastercard-logo.png";
import amex from "./Image/amex-card1708.jpg";
import rupay from "./Image/Rupay-Logo.png";
import CustomSpinner from "../CustomSpinner/CustomSpinner";

const inputStyle = {
  base: {
    color: "#ffffff",
    fontSize: "16px",
    fontFamily: "Inter, sans-serif",
    "::placeholder": { color: "rgba(255,255,255,0.4)" },
    iconColor: "#ff4135",
    backgroundColor: "transparent",
  },
  invalid: { color: "#ff4135", iconColor: "#ff4135" },
};

const cardNumberOptions = {
  style: inputStyle,
  showIcon: true,
};

const PaymentForm = ({ clientSecret, onSuccess, onError, tableNumber, amount, slot, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const formRef = useRef();

  // Handle clicks outside the form
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (formRef.current && !formRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage("");

    if (!stripe || !elements) return;

    const cardNumber = elements.getElement(CardNumberElement);
    const cardExpiry = elements.getElement(CardExpiryElement);
    const cardCvc = elements.getElement(CardCvcElement);

    if (!cardNumber || !cardExpiry || !cardCvc) {
      setErrorMessage("Card details are missing.");
      setLoading(false);
      return;
    }

    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardNumber,
      },
    });

    if (error) {
      setErrorMessage(error.message);
      onError(error.message);
    } else if (paymentIntent.status === "succeeded") {
      setPaymentSuccess(true);
      triggerConfetti();
      onSuccess(paymentIntent);
    }

    setLoading(false);
  };

  const triggerConfetti = () => {
    confetti({
      particleCount: 200,
      spread: 100,
      origin: { y: 0.6 },
    });
  };

  return (
    <div className="pf-form-wrapper" ref={formRef} style={{ 
      background: 'var(--tf-bg-card)', 
      border: '1px solid var(--tf-border)', 
      borderRadius: '24px', 
      overflow: 'hidden',
      display: 'flex', // Crucial: Restore flex layout
      width: '100%',
      minHeight: '550px',
      boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
      position: 'relative'
    }}>
      {paymentSuccess && <Confetti width={window.innerWidth} height={window.innerHeight} />}
      
      <button className="pf-close-btn" style={{ 
        position: 'absolute', top: '20px', right: '20px', background: 'transparent', border: 'none', 
        fontSize: '1.5rem', color: 'var(--tf-text-muted)', cursor: 'pointer', zIndex: 10 
      }} onClick={onClose}>
        <FaTimes />
      </button>

      <div className="pf-left-panel" style={{ 
        flex: '1', // Take half width
        padding: '3rem', 
        background: 'linear-gradient(135deg, #1b1c1f 0%, #292a2e 100%)', 
        borderRight: '1px solid var(--tf-border)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <div className="pf-header">
          <h2 className="pf-title" style={{ color: 'var(--tf-primary)', fontFamily: 'var(--tf-font-heading)', fontSize: '2.5rem', margin: 0, display: 'flex', alignItems: 'center' }}>
            Pay Securely <FaLock className="pf-lock-icon" style={{ fontSize: '1.2rem', marginLeft: '1rem', opacity: 0.5 }} />
          </h2>
          <p className="pf-subtitle" style={{ color: 'var(--tf-text-muted)', marginTop: '0.5rem', fontSize: '1.1rem' }}>Secure your visual reservation instantly</p>
        </div>

        <div className="pf-reservation-summary" style={{ 
          background: 'rgba(255,255,255,0.02)', 
          border: '1px solid rgba(255,255,255,0.05)',
          padding: '2rem',
          borderRadius: '16px'
        }}>
          <h3 style={{ color: 'var(--tf-primary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: '800', margin: '0 0 1.5rem 0' }}>Table Summary</h3>
          <div className="pf-summary-details">
            <div className="pf-detail-item" style={{ 
              display: 'flex', justifyContent: 'space-between', paddingBottom: '1rem', marginBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.05)' 
            }}>
              <span style={{ color: 'var(--tf-text-muted)' }}>Table:</span>
              <strong style={{ color: '#fff' }}>#{tableNumber}</strong>
            </div>
            <div className="pf-detail-item" style={{ 
              display: 'flex', justifyContent: 'space-between', paddingBottom: '1rem', marginBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.05)' 
            }}>
              <span style={{ color: 'var(--tf-text-muted)' }}>Timing:</span>
              <strong style={{ color: '#fff' }}>{ { 1: "5:00 - 7:00 PM", 2: "7:00 - 9:00 PM", 3: "9:00 - 11:00 PM" }[slot] || "N/A" }</strong>
            </div>
            <div className="pf-detail-item" style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '1rem' }}>
              <span style={{ color: 'var(--tf-text-muted)', fontSize: '1.2rem' }}>Total:</span>
              <strong style={{ color: 'var(--tf-primary)', fontSize: '2.2rem' }}>₹{amount}</strong>
            </div>
          </div>
        </div>

        <div className="pf-security-badges" style={{ display: 'flex', gap: '1rem' }}>
          <div className="pf-badge" style={{ 
            background: 'rgba(0, 209, 178, 0.05)', color: '#00d1b2', border: '1px solid rgba(0, 209, 178, 0.1)',
            padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
          }}>
            <FaShieldAlt className="pf-badge-icon" />
            <span>SSL SECURE</span>
          </div>
        </div>
      </div>

      <div className="pf-right-panel" style={{ 
        flex: '1.2', // Slightly wider for card form
        padding: '3rem', 
        background: 'var(--tf-bg-card)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        {!paymentSuccess ? (
          <>
            <form onSubmit={handleSubmit} className="pf-form" style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
              <div className="pf-input-group" style={{ marginBottom: '2rem' }}>
                <label style={{ color: 'var(--tf-text-muted)', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', marginBottom: '0.75rem', display: 'block' }}>
                   CARD NUMBER
                </label>
                <div className="pf-card-input-wrapper" style={{ 
                  background: 'var(--tf-bg-input)', border: '1px solid var(--tf-border)', padding: '1.25rem', borderRadius: '12px' 
                }}>
                  <CardNumberElement options={cardNumberOptions} />
                </div>
              </div>

              <div className="pf-input-row" style={{ display: 'flex', gap: '1.5rem', marginBottom: '2rem' }}>
                <div className="pf-input-group" style={{ flex: 1 }}>
                  <label style={{ color: 'var(--tf-text-muted)', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', marginBottom: '0.75rem', display: 'block' }}>EXPIRY</label>
                  <div className="pf-card-input-wrapper" style={{ 
                    background: 'var(--tf-bg-input)', border: '1px solid var(--tf-border)', padding: '1.25rem', borderRadius: '12px' 
                  }}>
                    <CardExpiryElement options={{ style: inputStyle }} />
                  </div>
                </div>

                <div className="pf-input-group" style={{ flex: 1 }}>
                  <label style={{ color: 'var(--tf-text-muted)', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', marginBottom: '0.75rem', display: 'block' }}>CVC</label>
                  <div className="pf-card-input-wrapper" style={{ 
                    background: 'var(--tf-bg-input)', border: '1px solid var(--tf-border)', padding: '1.25rem', borderRadius: '12px' 
                  }}>
                    <CardCvcElement options={{ style: inputStyle }} />
                  </div>
                </div>
              </div>

              {errorMessage && <div className="pf-error" style={{ 
                background: 'rgba(255, 65, 53, 0.1)', color: 'var(--tf-primary)', border: '1px solid rgba(255, 65, 53, 0.2)',
                padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', fontSize: '0.9rem'
              }}>{errorMessage}</div>}

              <button type="submit" disabled={!stripe || loading} className="tf-btn tf-btn-primary" style={{ width: '100%', height: '60px', fontSize: '1.2rem', fontWeight: '800' }}>
                {loading ? <CustomSpinner /> : `Confirm & Pay ₹${amount}`}
              </button>
            </form>

            <div className="pf-accepted-cards" style={{ marginTop: '3rem', opacity: 0.4, textAlign: 'center' }}>
              <div className="pf-card-brands" style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem' }}>
                <img src={visacard} alt="Visa" className="pf-card-brand-icon" style={{ width: '45px', filter: 'grayscale(1) invert(1)' }} />
                <img src={mastercard} alt="Mastercard" className="pf-card-brand-icon" style={{ width: '45px', filter: 'grayscale(1) invert(1)' }} />
                <img src={amex} alt="American Express" className="pf-card-brand-icon" style={{ width: '45px', filter: 'grayscale(1) invert(1)' }} />
                <img src={rupay} alt="RuPay" className="pf-card-brand-icon" style={{ width: '45px', filter: 'grayscale(1) invert(1)' }} />
              </div>
            </div>
          </>
        ) : (
          <div className="pf-success-container" style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="pf-success-message">
              <FaCheckCircle className="pf-success-icon" style={{ color: 'var(--tf-success)', fontSize: '5rem', marginBottom: '2rem' }} />
              <h3 style={{ color: 'var(--tf-text-white)', fontSize: '2.2rem', margin: '0 0 1rem 0' }}>Success!</h3>
              <p style={{ color: 'var(--tf-text-muted)', fontSize: '1.1rem' }}>Table #{tableNumber} is officially reserved for you.</p>
            </div>
            <button className="tf-btn tf-btn-outline" style={{ marginTop: '3rem', width: '100%', height: '54px' }} onClick={onClose}>Back to Planner</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentForm;